/**
 * Everything the club knows about one person.
 *
 * Opened from the membership panel rather than living on its own page:
 * the question "what size shirt does Mrs Whitfield take" is always asked
 * in the middle of doing something else, and making it a navigation
 * destination is how it stops being used.
 *
 * The fields are whatever the club has defined. This component knows the
 * five kinds a field can be and nothing at all about golf, shirts or
 * shellfish.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { date } from '../lib/format.ts';
import { ErrorBanner, Loading, Status } from './ui.tsx';
import { PhotoPicker } from './PhotoPicker.tsx';

interface Field {
  field_id: string;
  code: string;
  label: string;
  group_label: string;
  kind: 'text' | 'longtext' | 'number' | 'choice' | 'multichoice' | 'boolean';
  options: string[] | null;
  unit: string | null;
  placeholder: string | null;
  is_alert: boolean;
  value: string | null;
  updated_at: string | null;
}

interface Vehicle {
  id: string;
  plate: string;
  plate_region: string | null;
  make: string | null;
  model: string | null;
  color: string | null;
  is_primary: boolean;
}

export interface PersonIdentity {
  id: string;
  membership_person_id: string;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  salutation: string | null;
  email: string | null;
  phone: string | null;
  date_of_birth: string | null;
  role: string;
  may_charge: boolean;
  ends_on: string | null;
  /** May the club print this birthday — day and month — in a publication? */
  publish_birthday: boolean;
  publish_birthday_set_at: string | null;
  photo_uri: string | null;
  /** "Mr. Charles Whitfield" — rendered by the database. */
  label?: string;
}

export function PersonDetail({
  personId,
  person,
  vehicles,
  onChanged,
}: {
  personId: string;
  person: PersonIdentity;
  vehicles: Vehicle[];
  onChanged: () => void;
}) {
  const { atLeast } = useSession();
  const canEdit = atLeast('staff');
  const prefs = useApi<{ groups: { name: string; fields: Field[] }[] }>(
    `/v1/people/${personId}/preferences`,
    [personId],
  );

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [addingVehicle, setAddingVehicle] = useState(false);

  const save = useMutation(async (values: Record<string, string | null>) =>
    api(`/v1/people/${personId}/preferences`, { method: 'PUT', body: { values } }),
  );

  if (prefs.loading) return <Loading rows={3} />;
  if (prefs.error) return <ErrorBanner error={prefs.error} />;

  const groups = prefs.data?.groups ?? [];
  const dirty = Object.keys(draft).length > 0;

  const valueOf = (f: Field) => draft[f.code] ?? f.value ?? '';
  const set = (code: string, v: string) => setDraft({ ...draft, [code]: v });

  return (
    <div style={{ padding: '4px 0 16px' }}>
      <ErrorBanner error={save.error} />

      {/*
        The portrait, above the identity fields rather than beside them.
        It is what a member of staff looks at to recognize somebody
        walking through the door — 0036's own words — so it leads the
        record rather than sitting in a corner of it.
      */}
      <PhotoPicker
        endpoint={`/v1/people/${personId}/photo`}
        name={person.label ?? `${person.first_name} ${person.last_name}`}
        photoUri={person.photo_uri}
        label="Portrait"
        hint="Shown beside this person's name at the bar, the range and the gate."
        canEdit={canEdit}
        onChanged={onChanged}
      />

      <IdentityEditor person={person} onChanged={onChanged} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
        {groups.map((g) => (
          <div key={g.name}>
            <div className="muted small" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              {g.name}
            </div>

            {g.fields.map((f) => (
              <div key={f.code} style={{ marginBottom: 10 }}>
                <label style={{ display: 'block' }}>
                  <span className="muted small">
                    {f.label}
                    {f.unit ? ` (${f.unit})` : ''}
                    {f.is_alert && ' ⚠'}
                  </span>

                  {f.kind === 'multichoice' ? (
                    /*
                     * Checkboxes, not a multi-select list box. A native
                     * <select multiple> requires ctrl-clicking to add a
                     * second item and silently drops everything else the
                     * moment somebody plain-clicks, which on a list of
                     * twenty interests loses nineteen of them without
                     * saying anything. Twenty checkboxes take more room
                     * and cannot do that.
                     *
                     * The value is a JSON array of strings, validated
                     * against the club's own options by PREF-1 (0192).
                     */
                    <MultiChoice
                      value={valueOf(f)}
                      options={f.options ?? []}
                      disabled={!canEdit}
                      onChange={(v) => set(f.code, v)}
                    />
                  ) : f.kind === 'choice' ? (
                    <select
                      value={valueOf(f)}
                      disabled={!canEdit}
                      onChange={(e) => set(f.code, e.target.value)}
                    >
                      <option value="">—</option>
                      {(f.options ?? []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : f.kind === 'boolean' ? (
                    <select
                      value={valueOf(f)}
                      disabled={!canEdit}
                      onChange={(e) => set(f.code, e.target.value)}
                    >
                      <option value="">—</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : f.kind === 'longtext' ? (
                    <textarea
                      rows={2}
                      value={valueOf(f)}
                      disabled={!canEdit}
                      placeholder={f.placeholder ?? ''}
                      onChange={(e) => set(f.code, e.target.value)}
                      style={{ width: '100%' }}
                    />
                  ) : (
                    <input
                      value={valueOf(f)}
                      disabled={!canEdit}
                      placeholder={f.placeholder ?? ''}
                      onChange={(e) => set(f.code, e.target.value)}
                      style={{ width: '100%' }}
                    />
                  )}
                </label>
              </div>
            ))}
          </div>
        ))}

        <div>
          <div className="muted small" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Vehicles
          </div>

          {vehicles.length === 0 && !addingVehicle && (
            <p className="muted small" style={{ margin: '0 0 8px' }}>
              None on file. The valet stand looks members up by plate.
            </p>
          )}

          {vehicles.map((v) => (
            <div key={v.id} className="small" style={{ marginBottom: 6 }}>
              <span className="mono">{v.plate}</span>
              {v.plate_region && <span className="muted"> {v.plate_region}</span>}
              <span className="muted">
                {' · '}
                {[v.color, v.make, v.model].filter(Boolean).join(' ') || 'no description'}
              </span>
              {v.is_primary && <span className="muted"> · usual car</span>}
            </div>
          ))}

          {canEdit &&
            (addingVehicle ? (
              <AddVehicle
                personId={personId}
                onDone={() => {
                  setAddingVehicle(false);
                  onChanged();
                }}
                onCancel={() => setAddingVehicle(false)}
              />
            ) : (
              <button className="secondary small" onClick={() => setAddingVehicle(true)}>
                + Vehicle
              </button>
            ))}
        </div>
      </div>

      {canEdit && dirty && (
        <div className="row" style={{ marginTop: 14 }}>
          <button
            className="gold"
            disabled={save.busy}
            onClick={async () => {
              // Send only what changed. Sending the whole form back would
              // rewrite every field's updated_at, and "when did we last
              // ask" is the useful half of this record.
              const r = await save.run(draft);
              if (r) {
                setDraft({});
                prefs.reload();
                onChanged();
              }
            }}
          >
            {save.busy ? 'Saving…' : `Save ${Object.keys(draft).length} change${Object.keys(draft).length === 1 ? '' : 's'}`}
          </button>
          <button className="secondary" onClick={() => setDraft({})}>
            Discard
          </button>
        </div>
      )}

      {groups.some((g) => g.fields.some((f) => f.is_alert && f.value)) && (
        <p className="muted small" style={{ marginTop: 12 }}>
          ⚠ marks something the kitchen and the bar need to see. It is shown on the member
          record, not filed in here.
        </p>
      )}
    </div>
  );
}

/**
 * Many answers from one list.
 *
 * Stored as a JSON array of strings so a club can add an option with a
 * comma in it — "Wine, food and travel" — without every member who chose
 * it acquiring three interests, which is what a comma-joined string does
 * the first time somebody types one.
 *
 * A value that will not parse is treated as nothing chosen rather than
 * throwing: this is a record of what somebody likes, and a malformed row
 * from an old import must not take the member's whole record down with
 * it.
 */
function MultiChoice({
  value,
  options,
  disabled,
  onChange,
}: {
  value: string;
  options: string[];
  disabled: boolean;
  onChange: (next: string) => void;
}) {
  let chosen: string[] = [];
  try {
    const parsed: unknown = value ? JSON.parse(value) : [];
    if (Array.isArray(parsed)) chosen = parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    chosen = [];
  }

  const toggle = (option: string) => {
    const next = chosen.includes(option)
      ? chosen.filter((c) => c !== option)
      // Kept in the club's own order rather than the order they were
      // clicked, so two members with the same interests read the same.
      : options.filter((o) => o === option || chosen.includes(o));
    // Nothing chosen is nothing recorded, not an empty list: the PUT
    // treats an empty string as "delete this answer", and an empty
    // array would store "we asked and they said none".
    onChange(next.length === 0 ? '' : JSON.stringify(next));
  };

  return (
    <div className="multichoice">
      {options.map((o) => (
        <label key={o} className={chosen.includes(o) ? 'chip chip--on' : 'chip'}>
          <input
            type="checkbox"
            checked={chosen.includes(o)}
            disabled={disabled}
            onChange={() => toggle(o)}
          />
          <span>{o}</span>
        </label>
      ))}
    </div>
  );
}

function AddVehicle({
  personId,
  onDone,
  onCancel,
}: {
  personId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [plate, setPlate] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [color, setColor] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const add = useMutation(async () =>
    api<{ note: string | null }>(`/v1/people/${personId}/vehicles`, {
      method: 'POST',
      body: { plate, make: make || null, model: model || null, color: color || null },
    }),
  );

  return (
    <div style={{ marginTop: 8 }}>
      <ErrorBanner error={add.error} />
      {note && <Status tone="warning">{note}</Status>}
      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        <input
          value={plate}
          onChange={(e) => setPlate(e.target.value.toUpperCase())}
          placeholder="Plate"
          className="mono"
          style={{ maxWidth: 120 }}
          autoFocus
        />
        <input value={color} onChange={(e) => setColor(e.target.value)} placeholder="Color" style={{ maxWidth: 100 }} />
        <input value={make} onChange={(e) => setMake(e.target.value)} placeholder="Make" style={{ maxWidth: 110 }} />
        <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="Model" style={{ maxWidth: 110 }} />
      </div>
      <div className="row" style={{ marginTop: 8 }}>
        <button
          className="small"
          disabled={add.busy || plate.trim().length < 2}
          onClick={async () => {
            const r = await add.run();
            if (r) {
              if (r.note) setNote(r.note);
              onDone();
            }
          }}
        >
          {add.busy ? 'Saving…' : 'Add'}
        </button>
        <button className="secondary small" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// The age used to be worked out here, from the date of birth, in the
// browser. It is now computed once in the database and arrives with the
// person, because two implementations of "how old is somebody" disagree
// on exactly one day a year — their birthday — and that is the day it
// matters.

/**
 * The person themselves — the fields the club got wrong when it typed
 * them in, and the two decisions it makes about them afterwards.
 *
 * Deliberately at the top of the panel rather than behind an "Edit"
 * button. The commonest reason anybody opens a person is that something
 * about them is wrong, and a form that has to be unlocked first is a
 * form people work around by telephoning the office.
 *
 * Nothing here deletes anybody. Ending someone's place on a membership
 * is a date, because "who could sign for a drink in 2019" has to keep
 * having an answer — and because a child who leaves for college and
 * comes back is the ordinary case, not an edge one.
 */
function IdentityEditor({
  person,
  onChanged,
}: {
  person: PersonIdentity;
  onChanged: () => void;
}) {
  const { atLeast } = useSession();
  const canEdit = atLeast('staff');
  const canEndPlace = atLeast('manager');

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [confirmEnd, setConfirmEnd] = useState(false);

  const savePerson = useMutation(async (body: Record<string, unknown>) =>
    api(`/v1/people/${person.id}`, { method: 'PATCH', body }),
  );
  const savePlace = useMutation(async (body: Record<string, unknown>) =>
    api(`/v1/membership-people/${person.membership_person_id}`, { method: 'PATCH', body }),
  );

  const field = (key: keyof PersonIdentity) =>
    draft[key] ?? (person[key] as string | null) ?? '';
  const set = (key: string, v: string) => setDraft({ ...draft, [key]: v });
  const dirty = Object.keys(draft).length > 0;

  const commit = async () => {
    const body: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(draft)) {
      // An emptied field means "we do not know this", which is different
      // from an empty string and has to reach the database as null.
      body[k] = v.trim() === '' ? null : v.trim();
    }
    // A name is the one thing that cannot be blanked: every statement,
    // every charge slip and every locker label is addressed from it.
    if (body.first_name === null) delete body.first_name;
    if (body.last_name === null) delete body.last_name;

    const done = await savePerson.run(body);
    if (done) {
      setDraft({});
      onChanged();
    }
  };

  const ended =
    person.ends_on !== null && person.ends_on <= new Date().toISOString().slice(0, 10);

  return (
    <div
      style={{
        border: '1px solid var(--border, #d8d3c9)',
        borderRadius: 6,
        padding: '12px 14px',
        marginBottom: 18,
      }}
    >
      <ErrorBanner error={savePerson.error ?? savePlace.error} />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 10,
        }}
      >
        <label>
          <span className="muted small">Title</span>
          <input
            value={field('salutation')}
            disabled={!canEdit}
            placeholder="Mr."
            onChange={(e) => set('salutation', e.target.value)}
          />
        </label>
        <label>
          <span className="muted small">First name</span>
          <input
            value={field('first_name')}
            disabled={!canEdit}
            onChange={(e) => set('first_name', e.target.value)}
          />
        </label>
        <label>
          <span className="muted small">Goes by</span>
          <input
            value={field('preferred_name')}
            disabled={!canEdit}
            placeholder={person.first_name}
            onChange={(e) => set('preferred_name', e.target.value)}
          />
        </label>
        <label>
          <span className="muted small">Last name</span>
          <input
            value={field('last_name')}
            disabled={!canEdit}
            onChange={(e) => set('last_name', e.target.value)}
          />
        </label>
        <label>
          <span className="muted small">Date of birth</span>
          <input
            type="date"
            value={field('date_of_birth').slice(0, 10)}
            disabled={!canEdit}
            onChange={(e) => set('date_of_birth', e.target.value)}
          />
        </label>

        {/*
         * Whether the club may PRINT it, which is a different question
         * from whether the club may KNOW it.
         *
         * A member is generally content for the club to hold their date
         * of birth — it drives the junior and dependent rules and the
         * liquor license depends on it — and perfectly entitled to want
         * no part of appearing in the March birthday list. So this is a
         * consent, kept beside the date it is about, dated by the
         * database, and off until somebody says otherwise.
         *
         * Saved on its own rather than joining the draft: a checkbox
         * that needs a Save button pressed afterwards is a permission
         * somebody thinks they granted and did not.
         */}
        <label className="consent">
          <input
            type="checkbox"
            checked={person.publish_birthday}
            disabled={!canEdit || !person.date_of_birth}
            onChange={async (e) => {
              const done = await savePerson.run({ publish_birthday: e.target.checked });
              if (done) onChanged();
            }}
          />
          <span>
            <strong>May appear in the birthday list</strong>
            <span className="muted small">
              {!person.date_of_birth
                ? 'No date of birth on file.'
                : person.publish_birthday
                  ? `The day and the month — never the year. Agreed ${
                      person.publish_birthday_set_at
                        ? date(person.publish_birthday_set_at.slice(0, 10))
                        : 'at some point'
                    }.`
                  : 'Off until the member says otherwise. Nothing is printed.'}
            </span>
          </span>
        </label>
        <label>
          <span className="muted small">Email</span>
          <input
            type="email"
            value={field('email')}
            disabled={!canEdit}
            onChange={(e) => set('email', e.target.value)}
          />
        </label>
        <label>
          <span className="muted small">Telephone</span>
          <input
            value={field('phone')}
            disabled={!canEdit}
            onChange={(e) => set('phone', e.target.value)}
          />
        </label>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          marginTop: 12,
          flexWrap: 'wrap',
        }}
      >
        {canEdit && (
          <button className="gold small" disabled={!dirty || savePerson.busy} onClick={commit}>
            {savePerson.busy ? 'Saving…' : 'Save changes'}
          </button>
        )}
        {dirty && (
          <button className="secondary small" onClick={() => setDraft({})}>
            Discard
          </button>
        )}

        {canEndPlace && !ended && (
          <span style={{ marginLeft: 'auto' }}>
            {confirmEnd ? (
              <>
                <span className="small muted" style={{ marginRight: 8 }}>
                  End their place on this membership today?
                </span>
                <button
                  className="secondary small"
                  disabled={savePlace.busy}
                  onClick={async () => {
                    const done = await savePlace.run({
                      ends_on: new Date().toISOString().slice(0, 10),
                    });
                    if (done) {
                      setConfirmEnd(false);
                      onChanged();
                    }
                  }}
                >
                  Yes, end it
                </button>{' '}
                <button className="secondary small" onClick={() => setConfirmEnd(false)}>
                  No
                </button>
              </>
            ) : (
              <button
                className="secondary small"
                disabled={person.role === 'primary'}
                title={
                  person.role === 'primary'
                    ? 'The primary member cannot be removed from their own membership'
                    : 'Records the date they left. Nothing is deleted.'
                }
                onClick={() => setConfirmEnd(true)}
              >
                Remove from membership
              </button>
            )}
          </span>
        )}

        {canEndPlace && ended && (
          <span style={{ marginLeft: 'auto' }}>
            <span className="muted small" style={{ marginRight: 8 }}>
              Left {date(person.ends_on)}.
            </span>
            <button
              className="secondary small"
              disabled={savePlace.busy}
              onClick={async () => {
                const done = await savePlace.run({ ends_on: null });
                if (done) onChanged();
              }}
            >
              Put them back
            </button>
          </span>
        )}
      </div>
    </div>
  );
}
